import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wand2, Maximize2, PenTool, Coins, Wrench, Receipt, ArrowRight, Film, PlayCircle, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CreditsShop from "@/components/CreditsShop";

/**
 * Tools landing page — shared between admin and makelaar/MM accounts.
 *   - Admins: see ALL tools + global stats (granted / spent / cost)
 *   - Makelaars: see only the tools the admin has activated for them
 * Routes: /admin/tools (admin) and /tools (makelaar). Same component.
 */
const TOOL_ICONS = {
    photo_retouch: Wand2,
    upscale: Maximize2,
    outline_blur: PenTool,
    video_tour: Film,
};
const TOOL_ACCENTS = {
    photo_retouch: "#E5A93D",
    upscale: "#7C3AED",
    outline_blur: "#059669",
    video_tour: "#0EA5E9",
};
const TOOL_EXPLAINERS = {
    photo_retouch: "https://customer-assets.emergentagent.com/job_makelaar-booking/artifacts/dt0ozl02_Website%20demo%20video.m4v",
    outline_blur: "https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/1729fdcd749f76c8_Outline%20en%20blur.mp4",
};

export default function AdminTools() {
    const { user, refreshUser } = useAuth();
    const { t } = useTranslation();
    const isAdmin = user?.role === "admin";
    const [catalogue, setCatalogue] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(0);
    const [explainer, setExplainer] = useState(null);
    const location = useLocation();

    // Navbar credits badge links to /tools#bijtanken — scroll to the recharge
    // block once the catalogue (and thus the CreditsShop) is rendered.
    useEffect(() => {
        if (location.hash !== "#bijtanken" || loading) return;
        const timer = setTimeout(() => {
            document.getElementById("bijtanken")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
        return () => clearTimeout(timer);
    }, [location.hash, location.key, loading]);

    const refreshBalance = useCallback(async () => {
        try {
            const me = await api.get("/tools/me");
            setBalance(me.data?.credits_balance || 0);
        } catch (e) { /* noop */ }
        // Also refresh /auth/me so the Navbar badge stays in sync
        try { await refreshUser?.({ force: true }); } catch (e) { /* noop */ }
    }, [refreshUser]);

    useEffect(() => {
        (async () => {
            try {
                // /tools/catalogue works for everyone (filters server-side by tool_access)
                const cat = await api.get("/tools/catalogue");
                setCatalogue(cat.data.tools || []);
                if (isAdmin) {
                    const st = await api.get("/admin/tools/stats");
                    setStats(st.data);
                } else {
                    // Show personal balance for non-admins
                    const me = await api.get("/tools/me");
                    setBalance(me.data?.credits_balance || 0);
                }
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        })();
    }, [isAdmin]);

    if (loading) {
        return (
            <div className="min-h-screen bg-zinc-50 text-zinc-950 p-10" data-testid="admin-tools-loading">
                <p className="text-sm text-zinc-500">Tools laden…</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="admin-tools-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                <div className="flex items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#E5A93D] mb-3 flex items-center gap-2">
                            <Wrench className="h-3.5 w-3.5" /> {isAdmin ? "Admin · Tools" : "AI Tools"}
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                            {t("tools.page_title")}
                        </h1>
                        {isAdmin ? (
                            <p className="mt-3 text-sm text-zinc-500 max-w-xl">
                                AI-tools die makelaars kunnen gebruiken op hun foto&apos;s. Activeer per gebruiker via{" "}
                                <Link to="/admin/users" className="text-[#E5A93D] underline underline-offset-2">
                                    {t("navbar.users")}
                                </Link>.
                            </p>
                        ) : (
                            <p className="mt-3 text-sm text-zinc-500 max-w-xl">
                                {t("tools.page_subtitle")}
                            </p>
                        )}
                    </div>
                    {isAdmin && stats && (
                        <div className="hidden md:flex items-center gap-8 px-6 py-4 bg-white border border-zinc-200 rounded-sm">
                            <Stat label="Credits toegekend" value={stats.credits_total_granted} accent="#22c55e" />
                            <Stat label="Credits verbruikt" value={stats.credits_total_spent} accent="#E5A93D" />
                            <Stat
                                label="Totaal kosten"
                                value={`€ ${(stats.by_tool || []).reduce((s, x) => s + (x.cost_eur || 0), 0).toFixed(2)}`}
                                accent="#7C3AED"
                            />
                        </div>
                    )}
                    {!isAdmin && (
                        <div className="hidden md:flex items-stretch divide-x divide-zinc-200 px-1 bg-white border border-zinc-200 rounded-sm">
                            <div className="flex items-center gap-3 px-5 py-4">
                                <Coins className="h-5 w-5 text-[#22c55e]" />
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t("tools.your_credits")}</p>
                                    <p className="text-2xl font-semibold tabular-nums text-[#22c55e] leading-tight" data-testid="my-credits-balance">
                                        {balance}
                                    </p>
                                </div>
                            </div>
                            <Link
                                to="/tools/invoices"
                                data-testid="tools-page-invoices-link"
                                className="flex items-center gap-2 px-5 py-4 text-sm text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 transition group"
                            >
                                <Receipt className="h-4 w-4 text-zinc-500 group-hover:text-[#E5A93D] transition" />
                                {t("navbar.my_invoices")}
                            </Link>
                        </div>
                    )}
                </div>

                {catalogue.length === 0 && !isAdmin && (
                    <div className="bg-white border border-zinc-200 rounded-sm p-10 text-center" data-testid="no-tools-empty-state">
                        <Wrench className="h-10 w-10 mx-auto text-zinc-400 mb-4" />
                        <p className="text-sm text-zinc-600 mb-1">{t("tools.no_access_title")}</p>
                        <p className="text-xs text-zinc-500">{t("tools.no_access_desc")}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {catalogue.map((tool) => {
                        const Icon = TOOL_ICONS[tool.key] || Wand2;
                        const accent = TOOL_ACCENTS[tool.key] || "#E5A93D";
                        // Build the route — admin gets /admin/tools/x, makelaar gets /tools/x.
                        // The server-side catalogue gives us /tools/x; for admins, prefix it.
                        const baseRoute = tool.route || `/tools/${tool.key.replace("_", "-")}`;
                        const route = isAdmin ? baseRoute.replace("/tools/", "/admin/tools/") : baseRoute;
                        const stat = (stats?.by_tool || []).find((s) => s.tool_key === tool.key);
                        return (
                            <Link
                                key={tool.key}
                                to={route}
                                data-testid={`tool-card-${tool.key}`}
                                className="group relative overflow-hidden bg-white hover:bg-zinc-100 border border-zinc-200 hover:border-zinc-400 rounded-sm p-7 transition-all"
                            >
                                <div
                                    className="absolute -top-12 -right-12 h-32 w-32 rounded-full blur-3xl opacity-25 group-hover:opacity-40 transition-opacity"
                                    style={{ background: accent }}
                                />
                                <div className="relative">
                                    <div
                                        className="inline-flex items-center justify-center h-12 w-12 rounded-sm bg-zinc-100 mb-5"
                                        style={{ color: accent }}
                                    >
                                        <Icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="font-display text-2xl font-semibold tracking-tight mb-2">
                                        {t(`tools.tiles.${tool.key}.title`, { defaultValue: tool.label })}
                                    </h3>
                                    <p className="text-sm text-zinc-500 mb-5 leading-relaxed">
                                        {t(`tools.tiles.${tool.key}.desc`, { defaultValue: tool.description })}
                                    </p>
                                    <div className="flex items-center justify-between text-[11px] uppercase tracking-wider mb-5">
                                        <span className="inline-flex items-center gap-1.5 text-zinc-600">
                                            <Coins className="h-3 w-3" /> {tool.credit_cost} {t("tools.tiles.credit_one", { defaultValue: "credit" })}
                                        </span>
                                        {isAdmin && (
                                            <span className="text-zinc-500">
                                                {stat ? `${stat.jobs} runs · €${stat.cost_eur.toFixed(2)}` : "Nog niet gebruikt"}
                                            </span>
                                        )}
                                    </div>
                                    {/* Explicit CTA — the whole card is clickable but the
                                        button removes any ambiguity that this tile opens the
                                        tool. Drives activation: registrations with 0 first-run
                                        usage are converting at 0%. */}
                                    <div
                                        data-testid={`tool-open-${tool.key}`}
                                        className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-sm text-xs font-bold uppercase tracking-[0.16em] transition-all group-hover:gap-3"
                                        style={{
                                            background: accent,
                                            color: accent === "#E5A93D" ? "#0A0A0A" : "#FFFFFF",
                                            boxShadow: `0 0 0 1px ${accent}55, 0 8px 24px -8px ${accent}80`,
                                        }}
                                    >
                                        {t("tools.open_tool", { defaultValue: "Open deze tool" })}
                                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                    </div>
                                    {TOOL_EXPLAINERS[tool.key] && (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExplainer({ url: TOOL_EXPLAINERS[tool.key], title: t(`tools.tiles.${tool.key}.title`, { defaultValue: tool.label }) }); }}
                                            className="mt-2.5 inline-flex items-center justify-center gap-2 w-full h-10 rounded-sm text-[11px] font-bold uppercase tracking-[0.14em] border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400 hover:text-zinc-950 transition"
                                            data-testid={`tool-explainer-${tool.key}`}
                                        >
                                            <PlayCircle className="h-4 w-4" style={{ color: accent }} />
                                            {t("tools.explainer_btn", { defaultValue: "Bekijk de explainer video" })}
                                        </button>
                                    )}
                                </div>
                            </Link>
                        );
                    })}
                </div>

                {!isAdmin && catalogue.length > 0 && (
                    <div id="bijtanken">
                        <CreditsShop onPurchased={refreshBalance} />
                    </div>
                )}
            </div>
            {explainer && (
                <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setExplainer(null)} data-testid="tool-explainer-modal">
                    <div className="relative bg-white rounded-md p-4 sm:p-6 max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-4 mb-4">
                            <h3 className="font-display font-bold text-lg">{explainer.title}</h3>
                            <button type="button" onClick={() => setExplainer(null)} className="h-8 w-8 flex items-center justify-center rounded-sm bg-zinc-100 hover:bg-zinc-200 text-zinc-600" data-testid="tool-explainer-close">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <video src={explainer.url} controls autoPlay muted playsInline className="w-full max-h-[72vh] rounded-sm bg-black" data-testid="tool-explainer-video" />
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
            <p className="text-xl font-semibold tabular-nums" style={{ color: accent }}>
                {value}
            </p>
        </div>
    );
}
